import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { X, Upload, Download } from 'lucide-react';
import { notify } from '../../services/notification';
import ValidationResultsModal from '../../components/shared/ValidationResultsModal';
import UploadHistoryTable from './UploadHistoryTable';
import {
  uploadPincodeMasterAction,
  getPincodeUploadLogsAction,
  getPincodeUploadLogsDetailsAction,
  downloadPincodeErrorReportAction
} from '../../actions/pincodeAction';
import { constant } from '../../constants/constant';
import './PincodeUploadModal.scss';

const PincodeUploadModal = ({ open, onClose, onUploadSuccess }) => {
  const dispatch = useDispatch();
  const pageSize = constant?.paginationCount || 10;

  // Upload states
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Validation error modal states
  const [showValidation, setShowValidation] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [selectedLogId, setSelectedLogId] = useState(null);

  // Upload history states
  const [uploadHistory, setUploadHistory] = useState([]);
  const [uploadHistoryTotalCount, setUploadHistoryTotalCount] = useState(0);
  const [uploadHistoryCurrentPage, setUploadHistoryCurrentPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Calculate pagination
  const uploadHistoryTotalPages = useMemo(
    () => Math.max(1, Math.ceil((uploadHistoryTotalCount || 0) / pageSize)),
    [uploadHistoryTotalCount, pageSize]
  );

  const uploadHistoryPageWindow = useMemo(() => {
    const pages = [];
    const maxButtons = 7;
    if (uploadHistoryTotalPages <= maxButtons) {
      for (let i = 1; i <= uploadHistoryTotalPages; i++) pages.push(i);
      return pages;
    }

    const showLeftEllipsis = uploadHistoryCurrentPage > 4;
    const showRightEllipsis = uploadHistoryCurrentPage < uploadHistoryTotalPages - 3;

    pages.push(1);
    if (showLeftEllipsis) pages.push("left-ellipsis");

    const start = Math.max(2, uploadHistoryCurrentPage - 1);
    const end = Math.min(uploadHistoryTotalPages - 1, uploadHistoryCurrentPage + 1);

    for (let i = start; i <= end; i++) pages.push(i);

    if (showRightEllipsis) pages.push("right-ellipsis");
    pages.push(uploadHistoryTotalPages);

    return pages;
  }, [uploadHistoryCurrentPage, uploadHistoryTotalPages]);

  // Fetch upload history
  const fetchUploadHistory = async () => {
    try {
      setHistoryLoading(true);
      let query = "";
      const res = await dispatch(
        getPincodeUploadLogsAction(uploadHistoryCurrentPage, pageSize, query)
      );

      if (res?.status === 200) {
        const historyData = res?.data?.results?.data || [];
        setUploadHistory(Array.isArray(historyData) ? historyData : []);
        const count = res?.data?.count || 0;
        setUploadHistoryTotalCount(count);

        const lastPage = Math.max(1, Math.ceil(count / pageSize));
        if (uploadHistoryCurrentPage > lastPage) setUploadHistoryCurrentPage(lastPage);
      }
    } catch {
      notify.error("Failed to load upload history");
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch history on page change or modal open
  useEffect(() => {
    if (open) {
      fetchUploadHistory();
    }
  }, [uploadHistoryCurrentPage, open]);

  // Handle escape key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open && !showValidation) {
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, showValidation]);

  // Upload file handler
  const handleFileUpload = async (file) => {
    if (!file) return;

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await dispatch(uploadPincodeMasterAction(formData));

      if (response?.status === 200 || response?.status === 201) {
        notify.success(response?.data?.message || "Pincode master uploaded successfully");
        setSelectedFile(null);
        setUploadHistoryCurrentPage(1);
        await fetchUploadHistory();
        if (onUploadSuccess) {
          onUploadSuccess(); // Refresh main list
        }
      } else {
        if (response?.data?.errors) {
          setSelectedFile(null);
          const errorMessages = response.data.errors;
          const errorItems = Array.isArray(errorMessages)
            ? errorMessages.map((msg, idx) => ({
              id: idx,
              title: `Error ${idx + 1}`,
              message: msg,
            }))
            : Object.keys(errorMessages).map((key) => ({
              id: key,
              title: `Validation Error: ${key}`,
              message: Array.isArray(errorMessages[key])
                ? errorMessages[key][0]
                : errorMessages[key],
            }));
          setValidationErrors(errorItems);
          setShowValidation(true);
        } else {
          notify.error("Upload failed");
        }
      }
    } catch {
      notify.error("Upload failed");
    } finally {
      setUploadLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (
        file.type.includes("excel") ||
        file.type.includes("spreadsheet") ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls")
      ) {
        setSelectedFile(file);
        handleFileUpload(file);
      } else {
        notify.error(
          "Please upload only Excel files (.xlsx, .xls)"
        );
      }
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      handleFileUpload(file);
    }
  };

  // Template download
  const handleDownloadTemplate = () => {
    notify.info("Downloading template...");
    const link = document.createElement("a");
    link.href = "/file/pincode_master_template.xlsx";
    link.download = "pincode_master_template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Error report download
  const handleDownloadErrorReport = async (logId, originalFilename = null) => {
    try {
      const response = await dispatch(downloadPincodeErrorReportAction(logId));

      if (response && response.blob) {
        let filename = `pincode_error_report_${logId}.xlsx`;

        if (originalFilename) {
          filename = originalFilename;
        } else {
          const contentDisposition = response.responseHeaders?.get("content-disposition");
          if (contentDisposition) {
            let filenameMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/);
            if (!filenameMatch) {
              filenameMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/);
            }
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].trim().replace(/['"]/g, '');
            }
          }
        }

        const blobUrl = URL.createObjectURL(response.blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      notify.error("Failed to download error report");
    }
  };

  // View validation errors
  const handleViewValidation = async (logId) => {
    setSelectedLogId(logId);
    try {
      const response = await dispatch(getPincodeUploadLogsDetailsAction(logId));
      if (response?.status === 200) {
        const errorData = response.data?.data || [];
        if (errorData.length === 0) {
          setValidationErrors([
            {
              id: 'no-errors',
              title: 'No Errors',
              message: 'No error logs found for this upload.',
            },
          ]);
        } else {
          const modalErrors = errorData.map((error, index) => ({
            id: index + 1,
            title: `Error ${index + 1}`,
            message: error, // 👈 string directly
          }));

          setValidationErrors(modalErrors);
        }
        setShowValidation(true);
      } else {
        // Even if status is not 200, show error notification
        notify.error("Failed to load validation details");
      }
    } catch (error) {
      notify.error("Failed to load validation details");
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="pincode-upload-modal-overlay" onClick={onClose}>
        <div className="pincode-upload-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <h2>Upload Pincode Master</h2>
            <button className="close-btn" onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="modal-body">
            {/* Template Download Section */}
            <div className="template-section">
              <button
                className="template-download-btn"
                onClick={handleDownloadTemplate}
                disabled={uploadLoading}
              >
                <Download size={16} />
                Download Template
              </button>
              <span className="template-note">
                Download the Excel template with required fields and format
              </span>
            </div>

            {/* Drop Zone */}
            <div
              className={`drop-zone ${dragActive ? "active" : ""} ${uploadLoading ? "disabled" : ""
                }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => !uploadLoading && document.getElementById('pincode-modal-file-upload').click()}
            >
              <div className="drop-zone-content">
                {uploadLoading ? (
                  <div className="uploading">
                    <div className="spinner"></div>
                    <p>Uploading {selectedFile?.name}...</p>
                  </div>
                ) : (
                  <>
                    <div className="icon">
                      <Upload size={32} className="upload-icon" />
                    </div>
                    <div className="title">
                      Drop your Excel file here or click to browse
                    </div>
                    <div className="subtitle">
                      Supported formats: .xlsx, .xls (Max size: 10MB)
                    </div>
                    <div className="note">
                      Required fields: Pincode, State, City, District, APS Code, Country
                    </div>
                  </>
                )}
              </div>
              <input
                id="pincode-modal-file-upload"
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleFileInput}
                disabled={uploadLoading}
              />
            </div>

            {/* Upload History Section */}
            <div className="upload-history-section">
              <h3>Upload History</h3>
              <UploadHistoryTable
                loading={historyLoading}
                uploadHistory={uploadHistory}
                totalCount={uploadHistoryTotalCount}
                currentPage={uploadHistoryCurrentPage}
                pageSize={pageSize}
                totalPages={uploadHistoryTotalPages}
                pageWindow={uploadHistoryPageWindow}
                onDownloadErrorReport={handleDownloadErrorReport}
                onViewValidation={handleViewValidation}
                onPageChange={setUploadHistoryCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Validation Results Modal */}
      <ValidationResultsModal
        open={showValidation}
        totalErrors={validationErrors?.length}
        items={validationErrors}
        onClose={() => {
          setShowValidation(false);
          setValidationErrors([]);
          setSelectedLogId(null);
        }}
        onDownloadErrorReport={() =>
          handleDownloadErrorReport(selectedLogId)
        }
        onTryAgain={() => {
          setShowValidation(false);
          setSelectedFile(null);
        }}
        showDownloadButton={false}
      />
    </>
  );
};

export default PincodeUploadModal;
